import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso || ""; }
}

export default function AdminDigest() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recipient, setRecipient] = useState("");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/digest/runs`, { withCredentials: true });
      setRuns(res.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const body = recipient.trim() ? { recipient: recipient.trim() } : {};
      const res = await axios.post(`${API}/admin/digest/send`, body, { withCredentials: true });
      toast.success(`Digest sent (id: ${res.data.email_id?.slice(0, 8)}…)`);
      setRecipient("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <section data-testid="admin-digest">
      <h2 className="font-serif text-3xl md:text-4xl font-light mb-2 tracking-tight">
        Weekly digest
      </h2>
      <p className="text-sm text-[#0F1F4E]/70 mb-6 max-w-2xl leading-relaxed">
        A digest summarising bookings, messages, poll votes, newsletter sign-ups, and top blog
        posts is sent automatically every <strong>Monday at 8:00 AM Pacific</strong>. You can
        also send one manually below.
      </p>

      <div className="bg-white border border-[#D9CFA0] rounded-lg p-6 mb-10 max-w-2xl">
        <label className="block text-xs uppercase tracking-wider text-[#0F1F4E]/70 mb-2">
          Send to (optional — defaults to studio owner)
        </label>
        <div className="flex flex-wrap gap-3">
          <input
            type="email"
            data-testid="digest-recipient-input"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="bella@triplebtherapy.com"
            className="flex-1 min-w-[240px] px-4 py-3 border border-[#D9CFA0] rounded-md bg-white"
          />
          <button
            type="button"
            onClick={sendNow}
            disabled={sending}
            data-testid="digest-send-now-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0F1F4E] text-[#F8F1D8] text-xs uppercase tracking-wider hover:bg-[#142a66] disabled:opacity-60"
          >
            <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send digest now"}
          </button>
        </div>
        <p className="text-xs text-[#0F1F4E]/60 mt-3">
          Note: Resend test mode only delivers to your verified account email. To send to
          <code className="px-1">bella@triplebtherapy.com</code>, verify the
          <code className="px-1">triplebtherapy.com</code> domain at
          <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline ml-1">resend.com/domains</a>.
        </p>
      </div>

      <h3 className="font-serif text-2xl font-light mb-4">Send history</h3>
      {loading ? (
        <p className="text-[#0F1F4E]/70">Loading…</p>
      ) : runs.length === 0 ? (
        <p className="text-[#0F1F4E]/70 text-sm">No digests sent yet.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#D9CFA0] rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F2EBD3]">
              <tr className="text-left text-xs uppercase tracking-wider text-[#0F1F4E]/70">
                <th className="px-4 py-3">Sent</th>
                <th className="px-4 py-3">Recipient</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Counts</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r, i) => (
                <tr key={i} className="border-t border-[#D9CFA0]/70 align-top" data-testid={`digest-row-${i}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.sent_at)}</td>
                  <td className="px-4 py-3">{r.recipient}</td>
                  <td className="px-4 py-3">
                    <span className={r.status === "sent" ? "text-green-700" : "text-red-600"}>
                      {r.status}
                    </span>
                    {r.error && <div className="text-xs text-red-600 mt-1 max-w-md">{r.error}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {r.counts ? (
                      <span>
                        B {r.counts.bookings} · M {r.counts.contacts} · V {r.counts.votes} · N{" "}
                        {r.counts.subs} · Views {r.counts.page_views}
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
